import {
  CanonicalUserRole,
  trackCanonicalEvent,
} from "./mixpanelTracker";
import { CANONICAL_EVENTS } from "./canonicalEvents";

type AccountType = "viewer" | "model" | string;
type VerificationStepType = "id_check" | "liveness_check" | "face_match";

export const ONBOARDING_MIXPANEL_EVENTS = {
  ONBOARDING_STARTED: CANONICAL_EVENTS.ONBOARDING_STARTED,
  PROFILE_COMPLETED: CANONICAL_EVENTS.PROFILE_COMPLETED,
  VERIFICATION_STARTED: CANONICAL_EVENTS.VERIFICATION_STARTED,
  VERIFICATION_STEP_STARTED: CANONICAL_EVENTS.VERIFICATION_STEP_STARTED,
  VERIFICATION_STEP_COMPLETED: CANONICAL_EVENTS.VERIFICATION_STEP_COMPLETED,
  VERIFICATION_ATTEMPT_STARTED: CANONICAL_EVENTS.VERIFICATION_ATTEMPT_STARTED,
  VERIFICATION_FAILED: CANONICAL_EVENTS.VERIFICATION_FAILED,
  VERIFICATION_COMPLETED: CANONICAL_EVENTS.VERIFICATION_COMPLETED,
  ONBOARDING_COMPLETED: CANONICAL_EVENTS.ONBOARDING_COMPLETED,
} as const;

const DIDIT_ATTEMPT_COUNT_KEY = "didit_verification_attempt_count";
const DIDIT_FAILURE_DEDUP_KEY = "didit_last_tracked_failure_sig";

export const DIDIT_FAILURE_STATUSES = [
  "declined",
  "kyc_expired",
  "expired",
  "abandoned",
  "unknown",
  "pending",
] as const;

export function normalizeDiditStatus(
  status: string | undefined | null
): string {
  if (!status) {
    return "not_started";
  }
  return status.trim().toLowerCase().replace(/\s+/g, "_");
}

export function isDiditFailureStatus(status: string): boolean {
  return (DIDIT_FAILURE_STATUSES as readonly string[]).includes(
    normalizeDiditStatus(status)
  );
}

export function isDiditSuccessStatus(status: string): boolean {
  return normalizeDiditStatus(status) === "approved";
}

export function getDiditVerificationAttemptCount(): number {
  if (typeof window === "undefined") {
    return 0;
  }
  const raw = sessionStorage.getItem(DIDIT_ATTEMPT_COUNT_KEY);
  const parsed = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function incrementDiditVerificationAttemptCount(): number {
  const next = getDiditVerificationAttemptCount() + 1;
  if (typeof window !== "undefined") {
    sessionStorage.setItem(DIDIT_ATTEMPT_COUNT_KEY, String(next));
  }
  return next;
}

export function resetDiditVerificationFailureDedup(): void {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(DIDIT_FAILURE_DEDUP_KEY);
  }
}

function shouldTrackDiditFailureOnce(
  status: string,
  attemptNumber: number
): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  const signature = `${attemptNumber}:${normalizeDiditStatus(status)}`;
  if (sessionStorage.getItem(DIDIT_FAILURE_DEDUP_KEY) === signature) {
    return false;
  }
  sessionStorage.setItem(DIDIT_FAILURE_DEDUP_KEY, signature);
  return true;
}

function mapUserRole(accountType: AccountType): CanonicalUserRole {
  return accountType === "viewer" ? "viewer" : "creator";
}

export function trackOnboardingStarted(props: {
  account_type: AccountType;
  user_id?: string;
}): void {
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.ONBOARDING_STARTED,
    { path: "/choose-user-type", account_type: props.account_type },
    { user_id: props.user_id, user_role: mapUserRole(props.account_type) }
  );
}

export function trackProfileCompleted(props: {
  account_type: AccountType;
  user_id?: string;
  completion_score?: number;
}): void {
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.PROFILE_COMPLETED,
    {
      account_type: props.account_type,
      completion_score: props.completion_score ?? 100,
    },
    { user_id: props.user_id, user_role: mapUserRole(props.account_type) }
  );
}

export function trackVerificationStarted(props: { user_id?: string }): void {
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.VERIFICATION_STARTED,
    { verification_type: "creator_identity" },
    { user_id: props.user_id, user_role: "creator" }
  );
}

export function trackVerificationStepStarted(props: {
  user_id?: string;
  step_type: VerificationStepType;
}): void {
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.VERIFICATION_STEP_STARTED,
    { step_type: props.step_type },
    { user_id: props.user_id, user_role: "creator" }
  );
}

export function trackVerificationStepCompleted(props: {
  user_id?: string;
  step_type: VerificationStepType;
  result: "passed" | "failed";
  failure_reason?: string;
}): void {
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.VERIFICATION_STEP_COMPLETED,
    {
      step_type: props.step_type,
      result: props.result,
      failure_reason: props.failure_reason,
    },
    { user_id: props.user_id, user_role: "creator" }
  );
}

export function trackVerificationCompleted(props: {
  user_id?: string;
  result: "approved" | "rejected";
  attempt_number?: number;
}): void {
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.VERIFICATION_COMPLETED,
    {
      verification_type: "creator_identity",
      provider: "didit",
      result: props.result,
      attempt_number: props.attempt_number,
    },
    { user_id: props.user_id, user_role: "creator" }
  );
}

export type VerificationAttemptSource =
  | "user_click"
  | "auto_resume"
  | "retry_after_failure"
  | "session_recover";

export type VerificationFailureSource =
  | "status_poll"
  | "session_create"
  | "callback_redirect"
  | "api_error";

export function trackVerificationAttemptStarted(props: {
  user_id?: string;
  attempt_number: number;
  source: VerificationAttemptSource;
}): void {
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.VERIFICATION_ATTEMPT_STARTED,
    {
      verification_type: "creator_identity",
      provider: "didit",
      attempt_number: props.attempt_number,
      source: props.source,
    },
    { user_id: props.user_id, user_role: "creator" }
  );
}

export function trackVerificationFailed(props: {
  user_id?: string;
  status: string;
  failure_reason?: string;
  attempt_number?: number;
  failure_source: VerificationFailureSource;
}): void {
  const normalizedStatus = normalizeDiditStatus(props.status);
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.VERIFICATION_FAILED,
    {
      verification_type: "creator_identity",
      provider: "didit",
      status: normalizedStatus,
      failure_reason: props.failure_reason ?? normalizedStatus,
      attempt_number: props.attempt_number ?? getDiditVerificationAttemptCount(),
      failure_source: props.failure_source,
    },
    { user_id: props.user_id, user_role: "creator" }
  );
}

export function trackDiditVerificationFailureOutcome(props: {
  user_id?: string;
  status: string;
  failure_source: VerificationFailureSource;
  failure_reason?: string;
  attempt_number?: number;
}): void {
  const attemptNumber =
    props.attempt_number ?? getDiditVerificationAttemptCount();
  if (!shouldTrackDiditFailureOnce(props.status, attemptNumber)) {
    return;
  }

  trackVerificationFailed({
    user_id: props.user_id,
    status: props.status,
    failure_reason: props.failure_reason,
    attempt_number: attemptNumber,
    failure_source: props.failure_source,
  });
  trackVerificationStepCompleted({
    user_id: props.user_id,
    step_type: "id_check",
    result: "failed",
    failure_reason: props.failure_reason ?? normalizeDiditStatus(props.status),
  });
  trackVerificationCompleted({
    user_id: props.user_id,
    result: "rejected",
    attempt_number: attemptNumber,
  });
}

export function registerDiditVerificationAttempt(props: {
  user_id?: string;
  source: VerificationAttemptSource;
}): number {
  resetDiditVerificationFailureDedup();
  const attemptNumber = incrementDiditVerificationAttemptCount();
  trackVerificationAttemptStarted({
    user_id: props.user_id,
    attempt_number: attemptNumber,
    source: props.source,
  });
  return attemptNumber;
}

export function trackOnboardingCompleted(props: {
  account_type: AccountType;
  user_id?: string;
}): void {
  trackCanonicalEvent(
    ONBOARDING_MIXPANEL_EVENTS.ONBOARDING_COMPLETED,
    { account_type: props.account_type },
    { user_id: props.user_id, user_role: mapUserRole(props.account_type) }
  );
}
